import Swiper from 'swiper'
import { A11y, Keyboard, Navigation } from 'swiper/modules'
import { LitElement, html } from 'vendor/lit'
import { NostoHelper } from 'helpers/nosto'
import { enhanceSwiperAccessibility } from 'utils/swiper-accessibility'
// import { toggle, addToCart, reRenderShopifySections } from 'utils'

class HHRecentlyReviewed extends LitElement {
  constructor() {
    super()
    this.productId = this.getAttribute('product-id')
    this.nostoPlacementId = this.getAttribute('nosto-placement-id') || false
    this.sectionId = this.getAttribute('section-id') || false
    this.productSlides = []

    const targetButton =
      this.closest('[data-slider-type]').getAttribute('data-slider-type')
    this.buttonTarget = document.querySelector(
      `[data-target-type="${targetButton}"]`
    )
  }

  connectedCallback() {
    super.connectedCallback()

    if (!this.nostoPlacementId) {
      Shopify.Products.recordRecentlyViewed()
      Shopify.Products.showRecentlyViewed({
        howManyToShow: 4,
        wrapperId: `recently-viewed-products-${this.getAttribute(
          'section-id'
        )}`,
        section: this,
        onComplete: (wrapper, section) => {
          this.initSlider()
        },
      })
    } else {
      this.buttonTarget.classList.add('is-loading')
      this.buttonTarget.setAttribute('aria-disabled', 'true')
      this.waitForNostoToLoad()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  onLoadComplete() {
    this.buttonTarget.classList.remove('is-loading')
    this.buttonTarget.removeAttribute('aria-disabled')
  }

  waitForNostoToLoad() {
    let limitInterval = 0
    const checkInterval = setInterval(() => {
      if (typeof window.nostojs === 'function') {
        this.getRecentlyViewedProducts()
        clearInterval(checkInterval)
      } else if (limitInterval >= 10) {
        this.getRecentlyViewedProducts()
        clearInterval(checkInterval)
      }

      limitInterval++
    }, 1000)
  }

  get wrapper() {
    this.sliderWrapper =
      this.sliderWrapper ??
      this.querySelector(`#RecentlyViewed-${this.sectionId} .swiper-wrapper`)

    return this.sliderWrapper
  }

  loopProducts(realSlotIdRendered, products, count = 0, shown = 0) {
    count = count ? count : products.length - 1
    if (shown == 0) {
      this.productSlides = []
      this.wrapper.style.display = 'none'
    }
    if (shown <= count) {
      const url = products[shown].url.replace('https://brochuwalker.myshopify.com', 'https://www.brochuwalker.com')
      fetch(`${url}?section_id=api-product-card`).then(
        async (response) => {
          const product = await response.text()
          const fresh = document.createElement('div')
          fresh.innerHTML = product

          fresh
            .querySelector('.js-anchor')
            .setAttribute(
              'href',
              `${fresh
                .querySelector('.js-anchor')
                .getAttribute('href')}/?nosto=${realSlotIdRendered}`
            )

          this.productSlides.push(
            `<div class="swiper-slide">${
              fresh.querySelector('[data-api-content]').innerHTML
            }</div>`
          )

          shown++
          this.loopProducts(realSlotIdRendered, products, count, shown)
        }
      )
    } else {
      this.wrapper.innerHTML = this.productSlides.join('')
      NostoHelper.triggerMigration(`#${this.id}`, true)
      this.wrapper.style.display = 'inline-flex'
      this.initSlider()
      this.onLoadComplete()
    }
  }

  async getRecentlyViewedProducts() {
    if (this.nostoPlacementId && window.nostojs) {
      window.nostojs((api) => {
        api
          .defaultSession()
          .viewProduct(`${this.productId}`)
          .setPlacements(
            this.nostoPlacementId
              ? [this.nostoPlacementId]
              : api.placements.getPlacements()
          ) // If no placement ID present, dynamically fetch all available nosto placement/slot
          .load()
          .then(async (response) => {
            const { recommendations } = response.campaigns

            let products = {}
            let realSlotIdRendered = ''
            if (this.nostoPlacementId) {
              products = recommendations[this.nostoPlacementId].products
              realSlotIdRendered =
                recommendations[this.nostoPlacementId].result_id
            } else {
              for (const property in recommendations) {
                if (
                  property.includes('RecentlyViewed') ||
                  recommendations[property]['title'].includes('Recently Viewed')
                ) {
                  products = recommendations[property].products
                  realSlotIdRendered = recommendations[property].result_id
                  break
                }
              }
            }

            // Remove the current product to Recently viewed
            const productIndex = products.findIndex(
              (product) =>
                parseInt(product.product_id) === parseInt(this.productId)
            )

            if (productIndex >= 0) {
              products.splice(productIndex, 1)
            }

            if (products.length > 0) {
              this.style.display = 'block'
              this.loopProducts(realSlotIdRendered, products)
            } else {
              this.buttonTarget.style.display = 'none'
              this.style.display = 'none'
            }
          })
      })

      return
    }

    const productRecommendations = await getProductRecommendations({
      product_id: this.productId,
      limit: 1,
      intent: 'related',
    })

    if (productRecommendations.products.length === 0) return

    this.renderProductRecommendation(productRecommendations.products[0])
  }

  initSlider() {
    const swiper = new Swiper(this.querySelector('.recently-viewed-slider'), {
      modules: [A11y, Keyboard, Navigation],
      a11y: {
        enabled: true,
        containerRole: 'group',
        containerRoleDescriptionMessage: 'carousel',
        itemRoleDescriptionMessage: 'slide',
        prevSlideMessage: 'Previous recently viewed products',
        nextSlideMessage: 'Next recently viewed products',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      watchSlidesProgress: true,
      spaceBetween: 0,
      slidesPerView: 1,
      breakpoints: {
        320: {
          slidesPerView: 2,
        },
        480: {
          slidesPerView: 3,
        },
        900: {
          slidesPerView: 4,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    })
    enhanceSwiperAccessibility(swiper)
  }

  render() {
    return html`<slot></slot>`
  }
}

if (!customElements.get('hh-recently-reviewed')) {
  customElements.define('hh-recently-reviewed', HHRecentlyReviewed)
}
